import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Box, Button, Divider, LinearProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import unifiedSocket from '../../services/unifiedSocket';
import useAuth from '../../hooks/useAuth';
import {
  getVideoPreview,
  listVideoPreviews,
  mintVideoPreviewPublic,
  prepareVideoPreview,
  retryVideoPreview
} from '../../services/contentAiService';

function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function fmtMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const s = Math.round(n / 1000);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}

export default function VideoPreviewPlayer({ videoId, title, onVideoRef, onPreviewStatusChange, onPreviewReady, autoFocusOnReady = false }) {
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const companyId = currentUser?.companyId || 1;

  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [selectedPreviewId, setSelectedPreviewId] = useState('');
  const [preview, setPreview] = useState(null);
  const [publicUrls, setPublicUrls] = useState(null);
  const [snapshots, setSnapshots] = useState([]);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const shortsRef = useRef(null);
  const hlsShortsRef = useRef(null);
  const lastPreviewStatusRef = useRef('');
  const setVideoEl = useCallback((el) => {
    videoRef.current = el;
    if (typeof onVideoRef === 'function') onVideoRef(el);
  }, [onVideoRef]);

  const loadPreviews = useCallback(async () => {
    if (!videoId) return;
    const resp = await listVideoPreviews(videoId, 10);
    const list = Array.isArray(resp?.data) ? resp.data : [];
    setPreviews(list);
    if (!selectedPreviewId && list.length) {
      setSelectedPreviewId(String(list[0].id));
    }
  }, [videoId, selectedPreviewId]);

  const loadPreview = useCallback(async () => {
    if (!selectedPreviewId) return;
    const resp = await getVideoPreview(Number(selectedPreviewId));
    setPreview(resp?.data?.preview || null);
  }, [selectedPreviewId]);

  const mintPublic = useCallback(async (pid) => {
    const resp = await mintVideoPreviewPublic(Number(pid), 180);
    setPublicUrls(resp?.data?.urls || null);
    return resp?.data?.urls || null;
  }, []);

  const loadSnapshots = useCallback(async (urls) => {
    const indexUrl = urls?.snapshotsIndexUrl || null;
    if (!indexUrl) return;
    try {
      const resp = await fetch(indexUrl);
      const json = await resp.json();
      const items = Array.isArray(json?.items) ? json.items : [];
      const base = indexUrl.split('/snapshots/')[0] + '/snapshots/';
      setSnapshots(items.map(it => ({ t: it.t, url: `${base}${it.file}${indexUrl.includes('?token=') ? indexUrl.slice(indexUrl.indexOf('?token=')) : ''}` })));
    } catch (_) {
      setSnapshots([]);
    }
  }, []);

  const attachHls = useCallback((manifestUrl) => {
    const el = videoRef.current;
    if (!el || !manifestUrl) return;
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch {}
      hlsRef.current = null;
    }
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = manifestUrl;
      return;
    }
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(manifestUrl);
      hls.attachMedia(el);
      hlsRef.current = hls;
    } else {
      el.src = manifestUrl;
    }
  }, []);

  const attachHlsShorts = useCallback((manifestUrl) => {
    const el = shortsRef.current;
    if (!el || !manifestUrl) return;
    if (hlsShortsRef.current) {
      try { hlsShortsRef.current.destroy(); } catch {}
      hlsShortsRef.current = null;
    }
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = manifestUrl;
      return;
    }
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(manifestUrl);
      hls.attachMedia(el);
      hlsShortsRef.current = hls;
    } else {
      el.src = manifestUrl;
    }
  }, []);

  const refreshPlayable = useCallback(async () => {
    if (!preview?.id) return;
    const urls = await mintPublic(preview.id);
    if (!urls?.hlsMasterUrl) return;
    attachHls(urls.hlsMasterUrl);
    if (urls?.shortsHlsUrl) attachHlsShorts(urls.shortsHlsUrl);
    await loadSnapshots(urls);
  }, [preview, mintPublic, attachHls, attachHlsShorts, loadSnapshots]);

  useEffect(() => {
    loadPreviews().catch(() => {});
  }, [loadPreviews]);

  useEffect(() => {
    if (!selectedPreviewId) return;
    loadPreview().catch(() => {});
  }, [selectedPreviewId, loadPreview]);

  useEffect(() => {
    if (!preview?.id) return;
    if (String(preview.status) === 'completed') {
      refreshPlayable().catch(() => {});
    }
  }, [preview, refreshPlayable]);

  useEffect(() => {
    const statusKey = String(preview?.status || '').trim().toLowerCase();
    if (!statusKey) return;
    if (typeof onPreviewStatusChange === 'function') {
      onPreviewStatusChange({
        id: preview?.id ? Number(preview.id) : null,
        status: statusKey,
        preview: preview || null
      });
    }
    const previousStatus = String(lastPreviewStatusRef.current || '').trim().toLowerCase();
    if (statusKey === 'completed' && previousStatus !== 'completed') {
      if (typeof onPreviewReady === 'function') {
        onPreviewReady({
          id: preview?.id ? Number(preview.id) : null,
          status: statusKey,
          preview: preview || null
        });
      }
      if (autoFocusOnReady) {
        window.setTimeout(() => {
          const el = videoRef.current;
          if (!el) return;
          try {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch {}
          try { el.focus?.(); } catch {}
        }, 120);
      }
    }
    lastPreviewStatusRef.current = statusKey;
  }, [preview, onPreviewStatusChange, onPreviewReady, autoFocusOnReady]);

  useEffect(() => {
    if (!token || !socketUserId) return;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;

    const onUpdated = (payload) => {
      const pid = Number(payload?.previewId || 0);
      if (!pid || String(pid) !== String(selectedPreviewId)) return;
      loadPreview().catch(() => {});
    };
    socket.on('videoPreview.updated', onUpdated);
    return () => {
      try { socket.off('videoPreview.updated', onUpdated); } catch {}
    };
  }, [token, socketUserId, companyId, selectedPreviewId, loadPreview]);

  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch {}
        hlsRef.current = null;
      }
      if (hlsShortsRef.current) {
        try { hlsShortsRef.current.destroy(); } catch {}
        hlsShortsRef.current = null;
      }
    };
  }, []);

  const status = String(preview?.status || '');
  const pct = fmtPct(preview?.metaJson?.progress?.pct);
  const stage = preview?.metaJson?.progress?.stage || null;
  const issues = Array.isArray(preview?.metaJson?.issues) ? preview.metaJson.issues : [];

  const onPrepare = useCallback(async () => {
    if (!videoId) return;
    setLoading(true);
    try {
      await prepareVideoPreview(videoId);
      toast.success('Preview enfileirado');
      await loadPreviews();
      await loadPreview();
    } catch (e) {
      toast.error(e?.message || 'Falha ao preparar preview');
    } finally {
      setLoading(false);
    }
  }, [videoId, loadPreviews, loadPreview]);

  const onRetry = useCallback(async () => {
    if (!preview?.id) return;
    setLoading(true);
    try {
      await retryVideoPreview(preview.id);
      toast.success('Retry enfileirado');
      await loadPreview();
    } catch (e) {
      toast.error(e?.message || 'Falha ao retry');
    } finally {
      setLoading(false);
    }
  }, [preview, loadPreview]);

  const onFullscreen = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
  }, []);

  const onSeek = useCallback((t) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = Number(t || 0);
    el.play().catch(() => {});
  }, []);

  const header = useMemo(() => {
    const name = title || `Vídeo ${videoId}`;
    const pv = previews.find(p => String(p.id) === String(selectedPreviewId));
    const label = pv ? `#${pv.id} (${pv.status})` : '';
    return `${name} — Preview ${label}`;
  }, [title, videoId, previews, selectedPreviewId]);

  return (
    <Box display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h6">{header}</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" onClick={onPrepare} disabled={loading}>{loading && !preview?.id ? 'Preparando...' : 'Preparar preview'}</Button>
          <Button variant="outlined" onClick={refreshPlayable} disabled={loading || status !== 'completed'}>Atualizar player</Button>
          <Button variant="outlined" onClick={onRetry} disabled={loading || !preview?.id}>{loading && preview?.id ? 'Enfileirando...' : 'Retry'}</Button>
          <Button variant="contained" onClick={onFullscreen} disabled={!publicUrls?.hlsMasterUrl}>Fullscreen</Button>
        </Box>
      </Box>

      {(status === 'processing' || status === 'queued' || status === 'retrying') && (
        <Box>
          <Typography variant="body2">Status: {status}{stage ? ` (${stage})` : ''}</Typography>
          <LinearProgress variant="determinate" value={Math.round(pct * 100)} />
        </Box>
      )}

      {status === 'failed' && (
        <Typography variant="body2" color="error">Falhou: {preview?.lastError || 'erro'}</Typography>
      )}

      {issues.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Alertas</Typography>
          {issues.map((i, idx) => (
            <Typography key={idx} variant="body2">- {i.message || i.code}</Typography>
          ))}
        </Box>
      )}

      <Box display="grid" gap={1}>
        <video
          ref={setVideoEl}
          controls
          style={{ width: '100%', maxHeight: 520, background: '#000', borderRadius: 8 }}
          playsInline
        >
          {publicUrls?.subtitlesVttUrl ? (
            <track kind="subtitles" srcLang="pt" label="PT" src={publicUrls.subtitlesVttUrl} default />
          ) : null}
        </video>
        {publicUrls?.thumbnailUrl ? (
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Typography variant="body2">Thumbnail</Typography>
            <img alt="thumbnail" src={publicUrls.thumbnailUrl} style={{ height: 90, borderRadius: 6 }} />
            {publicUrls?.waveformUrl ? (
              <img alt="waveform" src={publicUrls.waveformUrl} style={{ height: 90, borderRadius: 6, background: '#111' }} />
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Divider />

      {previews.length > 1 ? (
        <Box>
          <Typography variant="subtitle2">Comparar versões</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {previews.map((p) => (
              <Button
                key={p.id}
                size="small"
                variant={String(p.id) === String(selectedPreviewId) ? 'contained' : 'outlined'}
                onClick={() => setSelectedPreviewId(String(p.id))}
              >
                #{p.id} {p.status}
              </Button>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box>
        <Typography variant="subtitle2">Timeline (snapshots)</Typography>
        <Box display="flex" gap={1} flexWrap="nowrap" overflow="auto" pb={1}>
          {snapshots.map((s, idx) => (
            <Box key={idx} sx={{ cursor: 'pointer' }} onClick={() => onSeek(s.t)}>
              <img alt={`t=${s.t}`} src={s.url} style={{ height: 70, borderRadius: 6 }} />
              <Typography variant="caption" display="block" textAlign="center">{fmtMs(s.t * 1000)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {publicUrls?.shortsHlsUrl ? (
        <Box>
          <Typography variant="subtitle2">Preview Shorts</Typography>
          <video
            ref={shortsRef}
            controls
            style={{ width: 260, height: 460, background: '#000', borderRadius: 8 }}
            playsInline
          />
        </Box>
      ) : null}
    </Box>
  );
}
